import React from 'react';
import { Box, Tabs, TabList, TabPanels, Tab, TabPanel, Badge, HStack, Text } from '@chakra-ui/react';
// `Text` stays for the no-access message below; the tab labels deliberately
// avoid it (see the TabList).
import { Navigate, useSearchParams } from 'react-router-dom';
import { usePermissions } from 'hooks/usePermissions';
import WhatsappSettings from './index';
import MetaWhatsappSettings from 'views/admin/meta-whatsapp/settings';
import MetaWhatsappAssignments from 'views/admin/meta-whatsapp/assignments';
import SenderPool from 'views/admin/meta-campaigns/senders';
import CampaignSettings from 'views/admin/meta-campaigns/settings';
import Suppressions from 'views/admin/meta-campaigns/suppressions';

/**
 * One WhatsApp settings page, three tabs.
 *
 * ★ ONE MODULE, ONE SCREEN, EACH TAB ON ITS OWN ACTION.
 * Classic and Meta WhatsApp are actions of the same `whatsapp` module, so a
 * tenant can still hand an agent the Meta inbox without the classic screens,
 * or the reverse — the grant is per action, not per module.
 *
 * A tab is mounted ONLY when its permission passes. That matters for the
 * classic tab in particular: its component redirects to the dashboard when the
 * caller lacks `whatsapp_settings`, which would kick a Meta-only administrator
 * off this page the moment it rendered.
 *
 * The active tab is in the URL (`?tab=meta`), so the sidebar, the settings hub
 * card and the old `/meta-whatsapp/settings` path can all land on the right one.
 */

const TABS = [
	{
		key: 'classic',
		label: 'WhatsApp',
		module: 'whatsapp',
		action: 'whatsapp_settings',
		render: () => <WhatsappSettings />,
	},
	{
		key: 'meta',
		label: 'Meta WhatsApp',
		beta: true,
		module: 'whatsapp',
		action: 'settings.view',
		render: () => <MetaWhatsappSettings embedded />,
	},
	{
		// ★ ITS OWN TAB, ON ITS OWN PERMISSION, ON PURPOSE.
		// Assignment used to be reachable only by scrolling the setup screen,
		// which put a weekly operational act behind a one-time wizard and left
		// people asking whether changing who answers a number meant reconnecting
		// Meta. It does not. `channel.assign` is a different grant from
		// `settings.view`, so a team lead can be given this and nothing else.
		key: 'assignments',
		label: 'Assignments',
		module: 'whatsapp',
		action: 'channel.assign',
		render: () => <MetaWhatsappAssignments />,
	},
	// ★ EACH ON ITS OWN GRANT. Seeing the sender pool is not the same right as
	// editing campaign defaults or reading the do-not-message list, so none of
	// these inherits visibility from the others.
	{
		key: 'pool',
		label: 'Sender pool',
		beta: true,
		module: 'whatsapp',
		action: 'sender.view',
		render: () => <SenderPool />,
	},
	{
		key: 'campaign-settings',
		label: 'Campaign defaults',
		beta: true,
		module: 'whatsapp',
		action: 'campaign.settings.view',
		render: () => <CampaignSettings />,
	},
	{
		key: 'suppressions',
		label: 'Do not message',
		beta: true,
		module: 'whatsapp',
		action: 'suppression.view',
		render: () => <Suppressions />,
	},
];

export default function WhatsappSettingsHub() {
	const { hasPermission } = usePermissions();
	const [params, setParams] = useSearchParams();

	const tabs = TABS.filter((t) => hasPermission(t.module, t.action));

	// The requested tab, if the caller may see it; otherwise the first they may.
	const requested = params.get('tab');
	const index = Math.max(0, tabs.findIndex((t) => t.key === requested));

	const onChange = (i) => {
		const next = new URLSearchParams(params);
		next.set('tab', tabs[i].key);
		setParams(next, { replace: true });
	};

	// The route filter already required at least one permission; this is the
	// belt to that brace, so a stale permission map degrades to a message
	// rather than an empty page.
	if (!tabs.length) {
		return (
			<Box pt={{ base: '130px', md: '80px', xl: '80px' }} px={{ base: '4', md: '6' }}>
				<Text fontSize="sm" color="text.muted">
					You do not have access to WhatsApp settings.
				</Text>
			</Box>
		);
	}

	return (
		<Box pt={{ base: '130px', md: '80px', xl: '80px' }} px={{ base: '4', md: '6' }} pb="8">
			{/* The theme's own soft-rounded tabs: accent text on the accent tint,
			    the same selected state every other settings screen has. */}
			<Tabs index={index} onChange={onChange} variant="soft-rounded" isLazy>
				<TabList mb="4" gap="2" flexWrap="wrap">
					{tabs.map((t) => (
						<Tab key={t.key} fontSize="sm" fontWeight="medium" px="4">
							<HStack spacing="2">
								{/* A span, not <Text>: the global <p> colour would override the
								    selected-tab colour and leave the label unreadable. */}
								<Box as="span">{t.label}</Box>
								{t.beta && (
									<Badge
										as="span"
										colorScheme="green"
										variant="subtle"
										fontSize="0.6rem"
										borderRadius="full"
										px="1.5"
									>
										Beta
									</Badge>
								)}
							</HStack>
						</Tab>
					))}
				</TabList>
				<TabPanels>
					{tabs.map((t) => (
						<TabPanel key={t.key} p="0">
							{t.render()}
						</TabPanel>
					))}
				</TabPanels>
			</Tabs>
		</Box>
	);
}

/**
 * The old standalone address for Meta settings. Kept so bookmarks and any
 * link written before the merge still land somewhere sensible.
 */
export function MetaSettingsRedirect() {
	return <Navigate to="/whatsapp/settings?tab=meta" replace />;
}
